import { Component, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SparklineChartComponent } from '../sparkline-chart/sparkline-chart.component';
import { SampleSizeNoteComponent } from '../sample-size-note/sample-size-note.component';
import { InfoIconComponent } from '../info-icon/info-icon.component';
import { isLowDataCount } from '../../../../client/components/analytics/shared/analytics-request.util';

// Ported from the 4 "ZONE 2" KPI tiles in DashboardContent.tsx (Overview) - the same card shape
// is used on Coverage, Analysis, Impact, Reach and Mentions in the design, so it's a shared
// component from the start rather than something copy-pasted onto Overview alone.
//
// Coverage's KPI row (ROW 0 in CoverageDashboard.tsx) uses a visibly different card shape - a
// top-right note, a font-black value (with an optional color override and unit suffix), and an
// embedded sparkline. Brands' KPI row (ROW 2 in BrandsDashboard.tsx) is a third shape again - a
// top-right icon instead of a sparkline, a smaller font-bold value, and an always-present muted
// footnote line. Media's KPI row (ROW 2 in MediaDashboard.tsx) is the icon shape again but WITH a
// sparkline too (replacing the design's decorative hand-drawn SVG paths, which weren't data-driven
// - just a plausible representative trend). Rather than fork more components, this one grows a
// template mode per shape: icon set => Brands-style (sparkline alongside it if sparklineValues is
// also set); else sparklineValues set => Coverage-style; else Overview's original markup, untouched.
@Component({
	selector: 'app-kpi-tile',
	standalone: true,
	imports: [MatIconModule, MatTooltipModule, SparklineChartComponent, SampleSizeNoteComponent, InfoIconComponent],
	templateUrl: './kpi-tile.component.html',
	styleUrl: './kpi-tile.component.scss',
})
export class KpiTileComponent {
	@Input({ required: true }) label!: string;
	// Фаза 5 "i" explanations - i18n key(s) into the ANALYTICS_INFO namespace; undefined (every call
	// site not yet updated) renders no icon at all, so this is purely additive. See InfoIconComponent
	// for infoExtraKey's purpose.
	@Input() infoKey?: string;
	@Input() infoExtraKey?: string;
	@Input({ required: true }) value!: string;
	// undefined (Brands-style only) renders deltaLabel as a plain muted line with no icon/color -
	// Brands' "Monitored Brands" KPI has no trend at all, just "1 Client · 5 Competitors".
	@Input() trendUp?: boolean;
	// Icon-mode only: overrides which arrow icon shows, independent of trendUp's color - e.g.
	// Topics' "Negative %" KPI drops (good news, emerald) but still shows a down arrow, not up.
	// Defaults to trendUp's own direction when not set.
	@Input() trendIconUp?: boolean;
	// Icon-mode only: overrides the trend row's text color, for the rare KPI that's neither
	// clearly good (emerald) nor bad (rose) news - e.g. Sentiment's "Neutral Mentions" KPI, whose
	// design uses a plain slate color regardless of trend direction. Defaults to the usual
	// trendUp-driven emerald/rose when not set.
	@Input() trendColorClass?: string;
	@Input({ required: true }) deltaLabel!: string;
	@Input() rangeText?: string;
	@Input() subLabel?: string;

	// No longer rendered (Правило В: the value is always text-ink) - kept only so older bindings
	// on not-yet-visited call sites don't fail; harmless once every screen stops passing it.
	@Input() valueColorClass = 'text-ink';

	// Opt-in exception to Правило В, plain mode only (no icon/sparklineValues): Overview's Net
	// Sentiment card is the one KPI whose raw value IS the finding - the same same-sign coloring
	// already used for per-brand netSentimentPercent (signTone) on the Brands screen. Undefined
	// keeps every other caller's default text-ink value untouched.
	@Input() valueTone?: 'positive' | 'negative' | 'neutral';

	// "Врз основа на N спомнувања" footnote - undefined (the default, every call site that hasn't
	// been updated yet) renders nothing, so this is purely additive. Set it to the KPI's own
	// ClientOverviewKpiMetric.sampleSize (or equivalent mentions count) at the call site.
	@Input() sampleSize?: number;

	// CLIPPING-threshold (2026-10-25): pales the big value text itself (never hides it) when it was
	// computed from under LOW_DATA_THRESHOLD mentions. SampleSizeNoteComponent reads the same
	// `sampleSize` to show its own badge, so the two always agree.
	get isLowSample(): boolean {
		return isLowDataCount(this.sampleSize);
	}

	get valueToneClass(): string {
		if (this.valueTone === 'positive') return 'text-positive';
		if (this.valueTone === 'negative') return 'text-negative';
		return 'text-ink';
	}

	// Coverage-style mode only:
	@Input() valueSuffix?: string;
	// Full class string (size + weight + color) - the design varies all three per card (e.g. a
	// bold colored "pts" next to a signed value vs. a plain light-gray "/100"), so this is left
	// to the caller rather than split into separate inputs.
	@Input() valueSuffixClass = 'caption text-ink-subtle';
	@Input() topRightNote?: string;
	// Optional trailing muted text after deltaLabel, e.g. "vs prev. 1,085" - used by Coverage
	// Analysis's and Brands' KPI rows, not by Coverage Dashboard's.
	@Input() deltaContext?: string;

	// KPI cards redesign (2026-11-01): the comparison detail - "(49 → 34) vs 01.07.2026 –
	// 30.09.2026" and similar - is no longer rendered as always-visible text next to the %
	// change. Each of the 7 screens builds that detail differently (some embed it straight into
	// `deltaLabel` as a trailing "(...)", e.g. Coverage Dashboard; others pass it separately via
	// `rangeText`/`deltaContext`/`subLabel`, e.g. Overview/Brands/Topics) - rather than changing
	// every screen's own KPI-building logic to match one shape, this strips any such embedded
	// "(...)" for the inline label and folds everything (embedded or separate) into one tooltip
	// string here, in the one shared component every screen already goes through.
	get shortDeltaLabel(): string {
		return this.deltaLabel.replace(/\s*\([^)]*\)\s*$/, '');
	}

	get deltaTooltip(): string {
		const parts: string[] = [];
		if (this.shortDeltaLabel !== this.deltaLabel) {
			parts.push(this.deltaLabel);
		}
		if (this.rangeText) parts.push(this.rangeText);
		if (this.deltaContext) parts.push(this.deltaContext);
		if (this.subLabel) parts.push(this.subLabel);
		return parts.join(' ');
	}

	@Input() sparklineValues?: number[];
	@Input() sparklineColor = '#244896';
	@Input() sparklineZeroLine = false;

	// Brands-style mode only:
	@Input() icon?: string;
	@Input() iconColorClass = 'text-ink-subtle';
	@Input() footnote?: string;
}
